// Индикатор свежести — сквозной признак **всех** поверхностей: левая половина
// нижней строки состояния меню (F-013, решение 066; в шапке его больше нет).
//
// ⚠⚠ Он не принадлежит `S-10` и не рисуется ею: `docs/SCREEN-MAP.md`, раздел `S-10` —
// «Индикатор свежести в шапке — сквозной признак всех поверхностей, а не
// элемент этой одной». Поэтому он живёт в каркасе и виден на каждом экране, а
// поверхность `S-10` — это то, куда он ведёт.
//
// ⚠⚠ **Слово берётся из словаря сервера по коду случая, экран печатает его
// дословно** (таск 72, решение оркестратора 106; подставляет его `dataState.ts`).
// Ни одного `switch` по случаю здесь нет и быть не может: слово, выбранное
// клиентом по коду, потребовало бы второй таблицы соответствий — и она
// разошлась бы со словарём, как расходится всё удвоенное.
//
// ⚠⚠ **Недостижимых случаев здесь не нарисовано ни одного.** В срезе 1а
// поднимаются два из четырёх: упавшая синхронизация и «синхронизации ещё не
// было» (`docs/SCREEN-MAP.md`, раздел `S-10`). «Свіжо» и «застаріло» требуют
// успешной синхронизации, которой в срезе нет, — и заглушки под них не стоит,
// потому что печатается пришедшая фраза, какой бы она ни была: механизм один
// на все четыре.
//
// ⚠ Пока состояние не получено, индикатор **не показывает «свіжо»** и молчит
// до ответа (`docs/SCREEN-MAP.md`, раздел `S-10`): показать и через секунду сменить хуже,
// чем не показать ничего, — первое человек уже прочитал и запомнил.

import { Link } from "@tanstack/react-router";

import { StatusIcon, toneText, type Tone } from "@/components/ui/status-icon";
import { uk } from "@/i18n/uk";
import { capital, fill, w } from "@/api/words";
import { HintButton, type HintScale } from "@/money/Hint";
import { formatCount, formatDay, formatTime } from "@/money/format";
import type { FreshAnswer, Shown } from "./api";

const words = uk.header.freshness;

/** День набора словами. Приходит датой без времени — «2026-08-31». */
const PLAIN_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function dayWords(day: string): string {
  // ⚠ Полдень, а не полночь: день набора киевский, и полночь UTC у любого
  // пояса восточнее лежит уже в соседних сутках. Показывать дату на сутки
  // мимо здесь нельзя — ею человек меряет возраст данных.
  if (!PLAIN_DAY.test(day)) return day;
  return formatDay(new Date(`${day}T12:00:00Z`));
}

/**
 * Чем фраза дополняется на экране.
 *
 * ⚠ Само `note_uk` — тоже серверные слова, и в рабочем режиме оно называет
 * время последней удачной синхронизации и что за это время не учтено
 * (`docs/SCREEN-MAP.md`, раздел `S-10`). Но в состоянии `П` сервер отдаёт в нём ровно ту
 * же мысль, что и во фразе, и напечатанные рядом две одинаковые строки читаются
 * как сбой. Поэтому подпись показывается **только если она говорит другое**, а
 * не «всегда» и не «по коду состояния».
 */
export interface Told {
  readonly indicator: string;
  readonly sync: { readonly kind: string; readonly note_uk?: string | null | undefined };
}

export function detailsOf(state: Told): string[] {
  const said: string[] = [];
  const note = (state.sync.note_uk ?? "").trim();
  if (note !== "" && note.toLowerCase() !== state.indicator.toLowerCase()) {
    said.push(note);
  }
  return said;
}

/**
 * Тон значка по коду случая — одна таблица на систему (шапка и сводка дашборда).
 * ⚠ Это не слово: слово печатается пришедшим из словаря. Тон — второй признак.
 */
export const FRESH_TONE: Readonly<Record<string, Tone>> = {
  fresh: "ok",
  stale: "warn",
  sync_down: "alarm",
  no_sync_yet: "pending",
};

/** Ступени шкалы свежести — от лучшей к худшей; «ще не синхронізовано» вне шкалы. */
const SCALE = ["fresh", "stale", "sync_down"] as const;

/**
 * Шкала свежести для «?»: три ступени словами словаря, текущая — по коду случая.
 *
 * ⚠ Порог «свіжо / застаріло» — интервал синхронизации из ответа сервера (граница
 * индикатора — сам интервал, `api/data_state.py`); не пришёл (шапка) — ступени
 * без порога, своего числа клиент не подставляет.
 */
export function freshScale(code: string | undefined, intervalMinutes: number | null): HintScale {
  const text: Readonly<Record<(typeof SCALE)[number], string>> = {
    fresh: words.scale.fresh,
    stale: words.scale.stale,
    sync_down: words.scale.down,
  };
  const at = SCALE.findIndex((one) => one === code);
  return {
    steps: SCALE.map((one) => ({ tone: FRESH_TONE[one] ?? "neutral", word: capital(w(one)), text: text[one] })),
    current: at < 0 ? null : at,
    ...(intervalMinutes === null ? {} : { note: fill(words.scale.limit, { minutes: formatCount(intervalMinutes) }) }),
  };
}

/** «дані DnTrade на 30.09, 12:50» — источник и время последней удачной синхронизации. */
export function dntradeAt(iso: string | null | undefined): string | undefined {
  if (iso == null) return undefined;
  const at = new Date(iso);
  return fill(words.source, { when: `${formatDay(at)}, ${formatTime(at)}` });
}

/** Тон по состоянию: код, а если его нет — отказ синхронизации по виду `С`. */
function toneOf(state: Told & { readonly code?: string }): Tone {
  if (state.code !== undefined) return FRESH_TONE[state.code] ?? "neutral";
  return state.sync.kind === "С" ? "alarm" : "neutral";
}

/** Час последней удачной синхронизации: «10:49». Не было — ничего. */
function timeOf(state: Told & { readonly sync: { readonly since?: string | null | undefined } }): string | null {
  const since = state.sync.since;
  return since == null ? null : formatTime(new Date(since));
}

/** Видимая фраза: значок + слово + час моноширинным. Хвост — в «?». */
function Said({ state }: { readonly state: Shown }) {
  const tone = toneOf(state);
  const time = timeOf(state);
  const age = state.code === "stale" ? state.sync.hours_ago : null;
  // Левая половина строки состояния подписана источником: «DnTrade: свіжо · 22:47».
  const source = <span className="shrink-0 text-ink-3">{uk.status.source}:</span>;
  // Спокойное состояние — нейтральный текст и приглушённая галочка; цвет — отклонению.
  const calm = tone === "ok";
  const ink = calm ? "text-ink-2" : toneText(tone);
  if (age !== null && age !== undefined) {
    // «застаріло · 7 год»: слово случая сервера и возраст вместо часа последней синхронизации.
    return (
      <>
        <StatusIcon tone={tone} muted={calm} />
        {source}
        <span data-numbers className={ink}>
          {state.indicator} · {fill(uk.status.hours, { n: formatCount(Math.max(1, Math.floor(age))) })}
        </span>
      </>
    );
  }
  return (
    <>
      <StatusIcon tone={tone} muted={calm} />
      {source}
      <span title={state.indicator} className={`min-w-0 truncate ${ink}`}>
        {state.indicator}
      </span>
      {time === null ? null : (
        <span data-numbers className="text-ink-2">
          · {time}
        </span>
      )}
    </>
  );
}

/**
 * Индикатор. `answer` `null` — ответа ещё нет, и подпись молчит.
 *
 * `at` — адрес поверхности `S-10`: есть — индикатор ведёт на неё. `closed` —
 * поверхность человеку не выдана (`НВ`): индикатор **остаётся**, перехода нет,
 * и на месте перехода строка о закрытом (`docs/SCREEN-MAP.md`, `S-10`, «Переход
 * на поверхность»; решение оркестратора 84). Мёртвым пунктом переход не рисуется
 * (`DEC-O527`, п. 1), имени пресета в строке нет (`G81`).
 */
export function Freshness({
  answer,
  at,
  closed = false,
}: {
  readonly answer: FreshAnswer | null;
  readonly at?: string | undefined;
  readonly closed?: boolean;
}) {
  // Вход ещё не состоялся: состояния данных не у кого спрашивать, и шапке
  // нечего сказать. Строки «неизвестно» здесь нет — она была бы неправдой.
  if (answer !== null && answer.kind === "no-session") return null;

  const line = "flex flex-wrap items-center gap-x-xs gap-y-2xs text-xs";
  // Живая строка состояния — одна строка без переноса внутри (слова сжимаются многоточием).
  const one = "flex min-w-0 flex-nowrap items-center gap-x-1.5 whitespace-nowrap text-xs";
  // Строка состояния: текст стоит на тех же 16 px, что значки меню; подсветка ссылки выходит
  // за него на 4 px (`-mx-2xs`), поэтому у левой половины нет лишнего отступа против правой.
  const PILL = "rounded-control";

  if (answer === null) {
    // ⚠ Скелет повторяет размер будущей строки, а не крутится кружком:
    // разметка не прыгает, когда подпись появится.
    return (
      <div aria-hidden="true" className="flex items-center gap-xs">
        <span className="size-dot rounded-full bg-skeleton" />
        <span className="h-xs w-3xl rounded bg-skeleton" />
      </div>
    );
  }

  if (answer.kind === "cannot-show") {
    // Слова словаря по коду случая: не ответила наша страница, а не DnTrade.
    // Отказ — сразу вслух (`alert`), как и отказ синхронизации.
    return (
      <p role="alert" className={`${line} rounded-md bg-alarm-wash px-xs py-2xs text-ink-2`}>
        <span className="flex items-center gap-xs font-medium text-alarm-ink">
          <StatusIcon tone="alarm" />
          {uk.status.failed}
        </span>
        <span>{answer.text_uk}</span>
      </p>
    );
  }

  if (answer.kind === "silent") {
    return (
      <p role="alert" className={`${line} rounded-md bg-alarm-wash px-xs py-2xs text-ink-2`}>
        <span className="flex items-center gap-xs font-medium text-alarm-ink">
          <StatusIcon tone="alarm" />
          {uk.status.failed}
        </span>
        <span>{words.silentCause} {words.silentWayOut}</span>
      </p>
    );
  }

  const state = answer.state;
  // ⚠ Живая область на тексте: сменилась свежесть — чтец скажет вежливо (`status`).
  // Вслух сбой синхронизации говорит дело плашки «Потребує уваги» (`sync_down`),
  // а не вторая тревога в строке состояния.
  const role = "status";
  const tail = detailsOf(state).join(" ");
  const source = dntradeAt(state.sync.since);
  const help = (
    <HintButton
      about={words.label}
      hint={words.hint}
      bare
      lead={tail === "" ? undefined : tail}
      more={{ scale: freshScale(state.code, null), ...(source === undefined ? {} : { source }) }}
    />
  );

  if (closed || at === undefined) {
    return (
      <div className="flex flex-col gap-2xs">
        <div className="flex items-center gap-2xs self-start">
          <p role={role} className={`${one} ${PILL}`}>
            <Said state={state} />
          </p>
          {help}
        </div>
        {closed ? (
          <p className="flex flex-wrap gap-x-xs text-xs text-ink-3">
            <span>{words.hiddenWhat}</span>
            <span>· {words.hiddenHow}</span>
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2xs">
      <div role={role} className="min-w-0">
        {/* ⚠ Имя ссылки — видимая фраза (WCAG 2.5.3), «куди веде» — чтецу
            следом, скрытым текстом, а не `aria-label` поверх видимого. */}
        <Link
          to={at}
          className={
            `${one} ${PILL} group relative -mx-2xs px-2xs py-1 transition-colors ` +
            "hover:bg-overlay"
          }
        >
          <Said state={state} />
          <span className="sr-only"> · {words.goTo}</span>
        </Link>
      </div>
      {help}
    </div>
  );
}
